import type { ReactNode, CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Skeleton } from './Skeleton';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: string;
  hintTitle?: string;
  icon?: ReactNode;
  variant?: 'amber' | 'rose' | 'slate' | 'orange' | 'stone' | 'cyan' | 'indigo' | 'emerald' | 'teal';
  /** Tints the value â€” fallback if variant is omitted. */
  tone?: 'default' | 'warning' | 'critical' | 'success';
  isLoading?: boolean;
  valueClassName?: string;
  labelClassName?: string;
  hintClassName?: string;
  /**
   * Where the rows behind this number live.
   *
   * Given one, the whole tile becomes a link, so clicking the number itself
   * navigates. The border and icon warm on hover â€” the only cue that it is a
   * target, since there is no arrow to say so.
   *
   * Only worth passing when the destination actually shows *these* rows. A tile
   * that lands somewhere the reader then has to filter by hand is worse than a
   * tile that does nothing.
   */
  to?: string;
  /**
   * What the link is for, read out in place of the tile's contents.
   *
   * Without this a screen reader announces "1,204, link" â€” the number without
   * the noun. Defaults to the label, which is right for "Active" but not for a
   * tile whose label alone is ambiguous.
   */
  linkLabel?: string;
  /**
   * Narrows the screen the tile is already on, rather than leaving it.
   *
   * The other half of `to`: some figures have a page behind them and some have
   * a filter. A tile that sets a filter must not be a link â€” the address does
   * not change, and a link that goes nowhere is a promise it does not keep.
   *
   * `isActive` draws it as the filter currently in force, so the reader can see
   * which figure they are looking through.
   */
  onClick?: () => void;
  isActive?: boolean;
  layout?: 'default' | 'stacked';
}

interface VariantStyle {
  cardClass: string;
  labelClass: string;
  valueClass: string;
  hintClass: string;
  iconStyle: CSSProperties;
  activeBorderClass: string;
}

const VARIANTS: Record<string, VariantStyle> = {
  orange: {
    cardClass: 'bg-orange-50/90 dark:bg-slate-900 border-orange-200/90 dark:border-slate-800 shadow-xs hover:border-orange-300 dark:hover:border-slate-700',
    labelClass: 'text-orange-800 dark:text-orange-400 font-bold',
    valueClass: 'text-orange-600 dark:text-orange-400 font-black',
    hintClass: 'text-orange-600/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#ea580c', color: '#ffffff' },
    activeBorderClass: '!border-orange-500 dark:!border-orange-400 ring-1 ring-inset ring-orange-500/30',
  },
  amber: {
    cardClass: 'bg-amber-50/90 dark:bg-slate-900 border-amber-200/90 dark:border-slate-800 shadow-xs hover:border-amber-300 dark:hover:border-slate-700',
    labelClass: 'text-amber-800 dark:text-amber-400 font-bold',
    valueClass: 'text-amber-600 dark:text-amber-400 font-black',
    hintClass: 'text-amber-700/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#d97706', color: '#ffffff' },
    activeBorderClass: '!border-amber-500 dark:!border-amber-400 ring-1 ring-inset ring-amber-500/30',
  },
  rose: {
    cardClass: 'bg-rose-50/90 dark:bg-slate-900 border-rose-200/90 dark:border-slate-800 shadow-xs hover:border-rose-300 dark:hover:border-slate-700',
    labelClass: 'text-rose-800 dark:text-rose-400 font-bold',
    valueClass: 'text-rose-600 dark:text-rose-400 font-black',
    hintClass: 'text-rose-600/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#e11d48', color: '#ffffff' },
    activeBorderClass: '!border-rose-500 dark:!border-rose-400 ring-1 ring-inset ring-rose-500/30',
  },
  stone: {
    cardClass: 'bg-stone-100/90 dark:bg-slate-900 border-stone-300/90 dark:border-slate-800 shadow-xs hover:border-stone-400 dark:hover:border-slate-700',
    labelClass: 'text-stone-700 dark:text-slate-400 font-bold',
    valueClass: 'text-stone-700 dark:text-slate-200 font-black',
    hintClass: 'text-stone-500 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#475569', color: '#ffffff' },
    activeBorderClass: '!border-stone-500 dark:!border-stone-400 ring-1 ring-inset ring-stone-500/30',
  },
  slate: {
    cardClass: 'bg-slate-50/90 dark:bg-slate-900 border-slate-200/90 dark:border-slate-800 shadow-xs hover:border-slate-300 dark:hover:border-slate-700',
    labelClass: 'text-slate-800 dark:text-slate-300 font-bold',
    valueClass: 'text-slate-700 dark:text-slate-200 font-black',
    hintClass: 'text-slate-600 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#0f172a', color: '#ffffff' },
    activeBorderClass: '!border-slate-500 dark:!border-slate-400 ring-1 ring-inset ring-slate-500/30',
  },
  indigo: {
    cardClass: 'bg-indigo-50/90 dark:bg-slate-900 border-indigo-200/90 dark:border-slate-800 shadow-xs hover:border-indigo-300 dark:hover:border-slate-700',
    labelClass: 'text-indigo-800 dark:text-indigo-400 font-bold',
    valueClass: 'text-indigo-600 dark:text-indigo-400 font-black',
    hintClass: 'text-indigo-600/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#4f46e5', color: '#ffffff' },
    activeBorderClass: '!border-indigo-500 dark:!border-indigo-400 ring-1 ring-inset ring-indigo-500/30',
  },
  cyan: {
    cardClass: 'bg-cyan-50/90 dark:bg-slate-900 border-cyan-200/90 dark:border-slate-800 shadow-xs hover:border-cyan-300 dark:hover:border-slate-700',
    labelClass: 'text-cyan-800 dark:text-cyan-400 font-bold',
    valueClass: 'text-cyan-600 dark:text-cyan-400 font-black',
    hintClass: 'text-cyan-600/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#0891b2', color: '#ffffff' },
    activeBorderClass: '!border-cyan-500 dark:!border-cyan-400 ring-1 ring-inset ring-cyan-500/30',
  },
  emerald: {
    cardClass: 'bg-emerald-50/90 dark:bg-slate-900 border-emerald-200/90 dark:border-slate-800 shadow-xs hover:border-emerald-300 dark:hover:border-slate-700',
    labelClass: 'text-emerald-800 dark:text-emerald-400 font-bold',
    valueClass: 'text-emerald-600 dark:text-emerald-400 font-black',
    hintClass: 'text-emerald-600/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#059669', color: '#ffffff' },
    activeBorderClass: '!border-emerald-500 dark:!border-emerald-400 ring-1 ring-inset ring-emerald-500/30',
  },
  teal: {
    cardClass: 'bg-teal-50/90 dark:bg-slate-900 border-teal-200/90 dark:border-slate-800 shadow-xs hover:border-teal-300 dark:hover:border-slate-700',
    labelClass: 'text-teal-800 dark:text-teal-400 font-bold',
    valueClass: 'text-teal-600 dark:text-teal-400 font-black',
    hintClass: 'text-teal-600/90 dark:text-slate-400 font-semibold',
    iconStyle: { backgroundColor: '#0d9488', color: '#ffffff' },
    activeBorderClass: '!border-teal-500 dark:!border-teal-400 ring-1 ring-inset ring-teal-500/30',
  },
};

/** A single headline number. Every dashboard tile in the product uses this. */
export function StatCard({
  label,
  value,
  hint,
  icon,
  variant,
  tone = 'default',
  isLoading = false,
  valueClassName,
  labelClassName,
  hintClassName,
  hintTitle,
  to,
  linkLabel,
  onClick,
  isActive = false,
  layout = 'default',
}: StatCardProps) {
  const selectedVariant = variant ?? (tone === 'warning' ? 'amber' : tone === 'critical' ? 'rose' : 'slate');
  const style = VARIANTS[selectedVariant] ?? VARIANTS.amber;

  const body =
    layout === 'stacked' ? (
      <div className="flex flex-col justify-between h-full gap-2">
        {/* Top row: Label and Icon in one div */}
        <div className="flex items-start justify-between gap-2">
          <div className=" flex flex-col gap-2">
          <p className={cn('text-xs sm:text-[14px] font-bold leading-tight min-w-0 flex-1', style.labelClass, labelClassName)}>
            {label}
          </p>
           {isLoading ? (
            <Skeleton className="mt-1 h-7 sm:h-8 w-20 rounded-lg" />
          ) : (
            <p className={cn(' text-2xl font-black tracking-tight tabular-nums truncate', style.valueClass, valueClassName)}>
              {value}
            </p>
          )}
          </div>
          {icon && (
            <span
              className="grid h-8 w-8 sm:h-9 sm:w-9 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs"
              style={style.iconStyle}
              aria-hidden
            >
              {icon}
            </span>
          )}
        </div>

        {/* Bottom row: Value and Hint in another div */}
        <div className="min-w-0">
         

          {hint && (
            <p className={cn('mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-bold line-clamp-1', style.hintClass, hintClassName)} title={hintTitle ?? hint}>
              {hint}
            </p>
          )}
        </div>
      </div>
    ) : (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className={cn('text-xs sm:text-[14px] font-bold leading-tight', style.labelClass, labelClassName)}>
            {label}
          </p>

          {isLoading ? (
            <Skeleton className="mt-1 h-7 sm:h-8 w-20 rounded-lg" />
          ) : (
            <p className={cn('mt-0.5 text-2xl font-black tracking-tight tabular-nums truncate', style.valueClass, valueClassName)}>
              {value}
            </p>
          )}

          {hint && (
            <p className={cn('mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-bold line-clamp-1', style.hintClass, hintClassName)} title={hintTitle ?? hint}>
              {hint}
            </p>
          )}
        </div>

        {icon && (
          <span
            className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs"
            style={style.iconStyle}
            aria-hidden
          >
            {icon}
          </span>
        )}
      </div>
    );

  const shell = cn(
    'group relative block overflow-hidden rounded-2xl sm:rounded-3xl border p-3.5 ',
    layout === 'stacked' && 'h-full',
    'shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200',
    style.cardClass,
    // The filter in force reads as pressed, not merely hovered â€” a refined border with subtle inner glow
    isActive && (style.activeBorderClass ?? '!border-indigo-500 dark:!border-indigo-400 ring-1 ring-inset ring-indigo-500/30'),
  );

  const interactive = 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent';

  if (to) {
    return (
      <Link to={to} aria-label={linkLabel ?? label} className={cn(shell, interactive)}>
        {body}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={isActive}
        aria-label={linkLabel ?? label}
        className={cn(shell, interactive, 'w-full text-left')}
      >
        {body}
      </button>
    );
  }

  return <div className={shell}>{body}</div>;
}
